import { useQuery, useMutation } from '@tanstack/react-query';
import { api, downloadFile } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { Card, Button, Table } from '../components/ui.js';
import { QueryState, ErrorState } from '../components/form.js';

interface ReportBundle {
  generatedAt: string;
  range: { from: string; to: string };
  disclaimer: string;
  patient: { name: string; nickname?: string | null; dob: string } | null;
  medications: { id: string; concept: string; brandName: string | null; approvalStatus: string }[];
  adherence: { adherencePct: number; totalScheduled: number; missed: number };
  labResults: { id: string; sampleDate: string; status: string; analytes: unknown[] }[];
  serology: { id: string }[];
  growth: { id: string }[];
  appointments: { id: string; date: string; examType: string }[];
  questions: { id: string; answered: boolean }[];
  attachments: { id: string }[];
}

export function Reports() {
  const { t, locale } = useI18n();

  const q = useQuery({
    queryKey: ['reports', 'data', locale],
    queryFn: () => api.get<ReportBundle>(`/reports/data?locale=${locale}`),
  });

  const downloadM = useMutation({
    mutationFn: ({ path, name }: { path: string; name: string }) => downloadFile(path, name),
  });

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-800 mb-4">{t('reports.title')}</h1>

      <Card title={t('reports.exports')}>
        <div className="flex flex-wrap gap-2 mb-3">
          <Button onClick={() => downloadM.mutate({ path: `/exports?format=json&locale=${locale}`, name: 'toxofollow-export.json' })}>JSON</Button>
          <Button onClick={() => downloadM.mutate({ path: `/exports?format=csv&locale=${locale}`, name: 'toxofollow-administrations.csv' })}>CSV</Button>
          <Button onClick={() => downloadM.mutate({ path: `/exports?format=xlsx&locale=${locale}`, name: 'toxofollow-export.xlsx' })}>XLSX</Button>
          <Button variant="secondary" onClick={() => downloadM.mutate({ path: `/exports?format=encrypted_zip&locale=${locale}`, name: 'toxofollow-export.zip.enc' })}>
            {t('reports.encryptedZip')}
          </Button>
          <Button variant="secondary" onClick={() => downloadM.mutate({ path: '/reminders/ics', name: 'toxofollow.ics' })}>
            ICS ({t('reports.icsCalendar')})
          </Button>
        </div>
        {downloadM.isError && <ErrorState error={downloadM.error} fallback={t('common.error')} />}
        <p className="text-xs text-slate-500 mt-2">
          {t('reports.pdfNotice')}
        </p>
      </Card>

      <Card title={t('reports.preview')}>
        <QueryState
          isLoading={q.isLoading}
          isError={q.isError}
          error={q.error}
          errorFallback={t('common.error')}
          isEmpty={!q.data}
          emptyMessage="—"
        >
          {q.data && (
            <div>
              <p className="text-xs text-slate-400 mb-3">
                {q.data.generatedAt?.slice(0, 16).replace('T', ' ')} · {q.data.range.from} → {q.data.range.to}
              </p>
              <Table headers={[t('common.section'), t('common.summary')]}>
                <tr className="border-b border-slate-100">
                  <td className="py-2 pr-4 font-medium">{t('reports.patient')}</td>
                  <td className="py-2 pr-4">{q.data.patient ? `${q.data.patient.nickname || q.data.patient.name} (${q.data.patient.dob})` : '—'}</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-2 pr-4 font-medium">{t('reports.medications')}</td>
                  <td className="py-2 pr-4">{q.data.medications.length}</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-2 pr-4 font-medium">{t('adherence.title')}</td>
                  <td className="py-2 pr-4">{q.data.adherence.adherencePct}% · {q.data.adherence.missed} {t('admin.missed')}</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-2 pr-4 font-medium">{t('exam.nfs')}</td>
                  <td className="py-2 pr-4">{q.data.labResults.length}</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-2 pr-4 font-medium">{t('exam.serology')}</td>
                  <td className="py-2 pr-4">{q.data.serology.length}</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-2 pr-4 font-medium">{t('growth.weight')}</td>
                  <td className="py-2 pr-4">{q.data.growth.length}</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-2 pr-4 font-medium">{t('appt.appointments')}</td>
                  <td className="py-2 pr-4">{q.data.appointments.length}</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-2 pr-4 font-medium">{t('weekly.questions')}</td>
                  <td className="py-2 pr-4">{q.data.questions.filter((x) => !x.answered).length} / {q.data.questions.length}</td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-2 pr-4 font-medium">{t('reports.attachments')}</td>
                  <td className="py-2 pr-4">{q.data.attachments.length}</td>
                </tr>
              </Table>
              <p className="text-xs text-slate-400 mt-3">{q.data.disclaimer}</p>
            </div>
          )}
        </QueryState>
      </Card>
    </div>
  );
}
